import React, { useRef, useState } from "react";
import { Box } from "@anchor/react-components/dist/lib/components";
import { Group } from "@anchor/react-components/dist/lib/components/Radio";
import Jira from "../PreventiveAndCorrectiveMeasures/Jira";
import PTask from "../PreventiveAndCorrectiveMeasures/PTask";
import errorNotification from "../Notification/errorNotification";
import successNotification from "../Notification/successNotification";

// No reachable trigger currently wires photo-evidence attach into Jira/PTask, so these
// stubs keep PhotoUploadModal from crashing without adding an unreachable code path here.
const NO_OP_IMAGES = { preventiveCorrectiveMeasures: { jira: [], ptask: [] } };

/**
 * Reuses the editor's Jira/PTask components inside the (read) report view, backed by a
 * local reducer + direct save instead of the full postmortem editor context/autosave stack.
 */
function ReportTrackingMethodManager({ postmortemData, incidentID, onSave }) {
  const existingPcm = (postmortemData?.preventiveCorrectiveMeasures && postmortemData.preventiveCorrectiveMeasures[0]) || {};

  const [pcmState, setPcmState] = useState({
    trackingMethod: existingPcm.trackingMethod || "Jira",
    jiraTickets: existingPcm.jira || [],
    pTasks: existingPcm.ptask || [],
  });
  // Jira/PTask call onAutoSave via setTimeout, so a ref avoids acting on a stale closure.
  const pcmStateRef = useRef(pcmState);

  const persist = async (nextState) => {
    try {
      await onSave({
        trackingMethod: nextState.trackingMethod,
        jira: nextState.jiraTickets,
        ptask: nextState.pTasks,
      });
      successNotification("Preventive & Corrective Measures updated successfully");
    } catch (error) {
      errorNotification("Failed to save changes, please try again");
    }
  };

  const handleRadioChange = (event) => {
    const next = { ...pcmStateRef.current, trackingMethod: event.target.value };
    pcmStateRef.current = next;
    setPcmState(next);
    persist(next);
  };

  // Mimics the subset of the editor's dispatch contract that Jira.jsx/PTask.jsx rely on.
  const dispatch = (action) => {
    if (!action || action.type !== "HANDLE EDITOR CHANGE") return;
    if (action.field !== "PreventiveAndCorrectiveMeasuresState") return;

    const payload = action.payload || {};
    const next = {
      trackingMethod: payload.trackingMethod ?? pcmStateRef.current.trackingMethod,
      jiraTickets: payload.jiraTickets ?? pcmStateRef.current.jiraTickets,
      pTasks: payload.pTasks ?? pcmStateRef.current.pTasks,
    };
    pcmStateRef.current = next;
    setPcmState(next);
  };

  const onAutoSave = () => {
    persist(pcmStateRef.current);
  };

  return (
    <>
      <Box p={6}>
        <Group
          fit="medium"
          id="report-tracking-method"
          label="Please select the tracking method"
          name="report-tracking-method"
          onChange={handleRadioChange}
          options={[
            { id: "Jira", label: "Jira", value: "Jira" },
            { id: "PTask", label: "PTask", value: "PTask" },
          ]}
          orientation="horizontal"
          value={pcmState.trackingMethod}
        />
      </Box>

      {pcmState.trackingMethod === "PTask" ? (
        <PTask
          contextState={pcmState}
          pTasks={pcmState.pTasks}
          dispatch={dispatch}
          images={NO_OP_IMAGES}
          imageFormData={null}
          incidentID={incidentID}
          currentStep={2}
          lockedFields={[]}
          changedFields={{}}
          onAutoSave={onAutoSave}
        />
      ) : (
        <Jira
          contextState={pcmState}
          jiraTickets={pcmState.jiraTickets}
          dispatch={dispatch}
          images={NO_OP_IMAGES}
          imageFormData={null}
          incidentID={incidentID}
          currentStep={2}
          lockedFields={[]}
          changedFields={{}}
          onAutoSave={onAutoSave}
        />
      )}
    </>
  );
}

export default ReportTrackingMethodManager;
